import { useCallback, useEffect, useState } from "react";
import {
  collectWeatherSourceMetrics,
  fetchWeatherFallbackSummary,
  fetchWeatherSourceMetricsSummary,
} from "../weather/weatherSourceMetricsApi.js";

export function useAdminWeatherSourceMetrics({ range = "24h", enabled = true } = {}) {
  const [summary, setSummary] = useState(null);
  const [fallbacks, setFallbacks] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  const load = useCallback(
    async ({ silent = false } = {}) => {
      if (!enabled) {
        return;
      }

      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      setErrorMessage("");

      try {
        const [summaryPayload, fallbackPayload] = await Promise.all([
          fetchWeatherSourceMetricsSummary({ range }),
          fetchWeatherFallbackSummary({ range }),
        ]);
        setSummary(summaryPayload);
        setFallbacks(fallbackPayload);
        setLastRefreshedAt(new Date().toISOString());
      } catch (error) {
        setSummary(null);
        setFallbacks(null);
        setErrorMessage(error.message || "Weather source metrics unavailable.");
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    },
    [enabled, range]
  );

  useEffect(() => {
    load();
  }, [load]);

  const runCollection = useCallback(async () => {
    await collectWeatherSourceMetrics();
    await load({ silent: true });
  }, [load]);

  return {
    summary,
    fallbacks,
    loading,
    refreshing,
    errorMessage,
    lastRefreshedAt,
    refresh: load,
    runCollection,
  };
}
